import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface FormData {
  name: string;
  email: string;
  businessName: string;
  webhookUrl: string;
}

export default function registerPage() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    businessName: '',
    webhookUrl: ''
  });
  const [result, setResult] = useState<{
    success: boolean;
    tenantId?: string;
    apiKey?: string;
    webhookSecret?: string;
    error?: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResult(null);
    setLoading(true);

    try {
      // For now, use the registration endpoint directly
      // In a full implementation, this would create both tenant and user
      const response = await fetch('/api/tenant/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setResult({
          success: true,
          tenantId: data.tenantId,
          apiKey: data.apiKey,
          webhookSecret: data.webhookSecret
        });
      } else {
        setResult({
          success: false,
          error: data.error || 'Registration failed'
        });
      }
    } catch (error: any) {
      setResult({
        success: false,
        error: error?.message || 'Registration failed'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-950 via-red-950 to-red-900 p-4">
      <div className="w-full max-w-lg">
        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-6">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">
              Register Tenant
            </h1>
            <p className="text-red-200">
              Create your merchant account to get started
            </p>
          </div>

          {result?.success ? (
            <div className="bg-green-500/20 border border-green-500 rounded-lg p-4 mb-6">
              <h3 className="text-green-500 font-semibold mb-2">Registration Successful!</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Tenant ID:</strong> {result.tenantId}</p>
                <p><strong>API Key:</strong> <code className="bg-black/30 px-2 py-1 rounded">{result.apiKey}</code></p>
                <p><strong>Webhook Secret:</strong> <code className="bg-black/30 px-2 py-1 rounded">{result.webhookSecret}</code></p>
              </div>
              <button
                className="w-full mt-4 bg-gradient-to-r from-red-600 via-red-500 to-red-600 hover:from-red-700 hover:via-red-600 hover:to-red-700 text-white font-medium py-2 px-4 rounded-lg transform transition-all duration-300 hover:scale-[1.03]"
                onClick={() => navigate('/login')}
              >
                Continue to Login
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Business Name
                </label>
                <input
                  type="text"
                  name="businessName"
                  placeholder="Your Business"
                  value={formData.businessName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Webhook URL (optional)
                </label>
                <input
                  type="url"
                  name="webhookUrl"
                  placeholder="https://your-domain.com/webhook"
                  value={formData.webhookUrl}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
                <p className="text-xs text-red-200 mt-1">
                  We'll send payment notifications here
                </p>
              </div>

              {result?.error && (
                <div className="bg-red-500/20 border border-red-500 text-red-500 px-4 py-3 rounded-lg text-sm">
                  {result.error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-red-600 via-red-500 to-red-600 hover:from-red-700 hover:via-red-600 hover:to-red-700 disabled:from-red-800 disabled:via-red-700 disabled:to-red-800 text-white font-medium py-3 px-4 rounded-lg transform transition-all duration-300 hover:scale-[1.03] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>

              <p className="text-center text-sm text-red-200 mt-4">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-red-400 hover:text-red-300 font-medium"
                >
                  Sign in
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
